import { useQuery } from "react-query";
import {
  collection,
  deleteDoc,
  doc,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import { db, createQuery } from "../providers/database";
import { collectionNames } from "./dictionary/collectionNames";
import { uploadGeneric } from "../providers/firebaseStorage";

export type AcademyListingStatus = "published" | "draft";

export type AcademyListing = {
  id: string;
  order: number;
  status: AcademyListingStatus;
  kicker: string;
  name: string;
  desc: string;
  meta: string;
  cover: string;
  href: string;
  updated: string;
};

const LISTINGS = collectionNames.academyListings;

export function formatAcademyDate(date = new Date()) {
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
}

export function sortAcademyListings(rows: AcademyListing[] = []) {
  return [...rows].sort((a, b) => (a.order || 0) - (b.order || 0));
}

export function useAcademyListings() {
  return useQuery(
    ["academyListings"],
    createQuery(() => query(collection(db, LISTINGS))),
    { enabled: true },
  );
}

/** Published listings only. This is what the seeker Academy tab shows. */
export function usePublishedAcademyListings() {
  return useQuery(
    ["academyListings", "published"],
    createQuery(() =>
      query(collection(db, LISTINGS), where("status", "==", "published")),
    ),
    { enabled: true },
  );
}

export async function persistAcademyCover(id: string, cover: string) {
  if (!cover || !cover.startsWith("data:")) return cover || "";
  const response = await fetch(cover);
  const blob = await response.blob();
  const ext = (blob.type.split("/")[1] || "png").replace("jpeg", "jpg");
  const file = new File([blob], `cover.${ext}`, { type: blob.type || "image/png" });
  return uploadGeneric(file, "academyListings", `${id}-${Date.now()}.${ext}`);
}

export async function saveAcademyListing(listing: AcademyListing) {
  const { id, ...rest } = listing;
  await setDoc(doc(db, LISTINGS, id), { id, ...rest });
}

export async function removeAcademyListing(id: string) {
  await deleteDoc(doc(db, LISTINGS, id));
}
